/**
 * GitHub OAuth Button Component
 * @implements FS-001.2.1 - GitHub OAuth Authentication
 * @implements FS-001.5.2 - Return URL handling after OAuth login
 *
 * A reusable button component that initiates GitHub OAuth authentication.
 * Redirects to the backend OAuth endpoint which handles state cookie generation
 * and redirects to GitHub's authorization page.
 */

import { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';

/**
 * Props for the GitHubOAuthButton component
 */
export interface GitHubOAuthButtonProps {
  /** The OAuth flow type - determines button text and backend handling */
  flow: 'login' | 'register_po';
  /** Optional callback fired when authentication starts (before redirect) */
  onAuthStart?: () => void;
  /** Whether the button should be disabled */
  disabled?: boolean;
  /** Invitation code for PO registration flow */
  invitationCode?: string;
  /** Return URL to redirect to after successful authentication */
  returnUrl?: string;
}

/**
 * GitHub OAuth button that initiates authentication by redirecting to the backend.
 *
 * The button displays:
 * - "Sign in with GitHub" for login flow
 * - "Continue with GitHub" for registration flows
 *
 * On click, it redirects to `/auth/github?flow={flow}` which is handled by the
 * backend. The backend generates a state cookie and redirects to GitHub.
 *
 * @implements FS-001.2.1 - GitHub OAuth Authentication
 *
 * @example
 * ```tsx
 * // Login flow
 * <GitHubOAuthButton flow="login" />
 *
 * // PO registration with invitation code
 * <GitHubOAuthButton
 *   flow="register_po"
 *   invitationCode="abc123"
 * />
 * ```
 */
export const GitHubOAuthButton = ({
  flow,
  onAuthStart,
  disabled,
  invitationCode,
  returnUrl,
}: GitHubOAuthButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle button click - initiate OAuth flow
   * @implements FS-001.2.1 - GitHub OAuth redirect to backend
   * @implements FS-001.5.2 - Return URL handling for session expiration flow
   */
  const handleClick = () => {
    setIsLoading(true);
    onAuthStart?.();

    // Build query parameters for the OAuth redirect
    const params = new URLSearchParams({ flow });
    if (invitationCode) {
      params.set('invitationCode', invitationCode);
    }
    // Pass returnUrl to backend so it can redirect after OAuth callback
    if (returnUrl) {
      params.set('returnUrl', returnUrl);
    }

    // Redirect to backend OAuth endpoint
    // Backend handles state cookie generation and redirects to GitHub
    window.location.href = `/auth/github?${params.toString()}`;
  };

  // Button text varies based on flow type
  const buttonText =
    flow === 'login' ? 'Sign in with GitHub' : 'Continue with GitHub';

  return (
    <Button
      variant="contained"
      onClick={handleClick}
      disabled={disabled || isLoading}
      startIcon={
        isLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <GitHubIcon />
        )
      }
      fullWidth
      size="large"
      sx={{
        // GitHub branding colors
        backgroundColor: '#24292e',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#1b1f23',
        },
        '&:disabled': {
          backgroundColor: '#6a737d',
          color: '#ffffff',
        },
        // Ensure proper text styling
        textTransform: 'none',
        fontWeight: 500,
      }}
    >
      {isLoading ? 'Redirecting...' : buttonText}
    </Button>
  );
};
